/**
 * Pilot CustomEvent registry — the canonical source of truth for every
 * window-level event the React tree and the surviving `lib/pilot/*.js`
 * engine use to talk to each other.
 *
 * F1 of the Oatmeal rebuild (`docs/eng/pilot-oatmeal-rebuild.md`).
 * Previously these event names lived as string literals scattered
 * across the dispatcher and listener sites (main.js, Pilot.tsx,
 * Sandbox.tsx, Story.tsx, PilotContext.tsx, intake-bridge.ts). A typo
 * in either side silently broke the bridge. Centralizing them here:
 *
 *   - each name is exported as a `const` so TS catches usage typos;
 *   - the `detail` shape (when there is one) is exported as an
 *     interface so dispatchers and listeners agree on the payload;
 *   - the JSDoc records who dispatches, who listens, and what the
 *     event means.
 *
 * **`mp:*` legacy event names are kept verbatim.** They were named
 * before the `pilot:` prefix convention and are still emitted /
 * consumed by both React and the engine; renaming them is a
 * follow-up to F7's main.js teardown.
 *
 * Two existing modules pre-date this registry and own their own
 * constants for backwards-compatibility:
 *   - `intake-bridge.ts` — INTAKE_CHANGED_EVENT, INTAKE_SET_REQUEST_EVENT
 *   - `components/pilot/PilotContext.tsx` — PILOT_AUTH_CHANGED_EVENT
 * Their values are re-exported here for completeness.
 */

import {
  INTAKE_CHANGED_EVENT as INTAKE_CHANGED_EVENT_RAW,
  INTAKE_SET_REQUEST_EVENT as INTAKE_SET_REQUEST_EVENT_RAW,
  type IntakeSetRequestDetail,
} from './intake-bridge';

/* ============================================================
   React <-> engine event names
   ============================================================ */

/**
 * Engine → React. Fired by `main.js` when a process is loaded
 * (either via picker click or restore-on-reload). Carries the
 * picked process id so React-owned PilotContext can update
 * `currentProcessId`.
 *
 * Listeners: `Sandbox.tsx` (translates to `selectProcess()` on context).
 */
export const PILOT_PROCESS_SELECTED_EVENT = 'pilot:process-selected';

export interface PilotProcessSelectedDetail {
  processId: string;
}

/**
 * Engine → React. Imperative engine code requests a view-mode
 * transition by dispatching this event. The React bridge in
 * `Pilot.tsx` translates `detail` into `setViewMode()` on context.
 *
 * Dispatchers: `main.js` (mp:unlock handler, sandbox-cta button).
 * Listeners: `Pilot.tsx`.
 */
export const PILOT_SET_VIEW_MODE_EVENT = 'pilot:set-view-mode';

/** ViewMode literal payload (string detail, not wrapped in an object). */
export type PilotSetViewModeDetail =
  | 'story'
  | 'sandbox'
  | 'pitch'
  | 'demo'
  | 'vision';

/**
 * React → engine. Sandbox FAB click asks main.js to open the
 * "try another process" modal. Until the modal is React-owned, this
 * event is the bridge.
 *
 * Dispatchers: `Sandbox.tsx` (openChangeModal).
 * Listeners: `main.js`.
 */
export const PILOT_OPEN_CHANGE_PROCESS_MODAL_EVENT =
  'pilot:open-change-process-modal';

/**
 * React → engine. Pitch deck's "Book a conversation" CTA asks main.js
 * to open the talk-to-us modal. Phase 4 of the Oatmeal rebuild made
 * `<Pitch />` conditionally mount on `viewMode === 'pitch'`, which
 * means main.js's one-shot `#pitch-talk-cta.onclick = openTalkModal`
 * binding at startup fires before the element exists. The event
 * replaces that binding so the CTA wires correctly on every Pitch
 * mount.
 *
 * Dispatchers: `Pitch.tsx` (#pitch-talk-cta onClick).
 * Listeners: `main.js`.
 */
export const PILOT_OPEN_TALK_MODAL_EVENT = 'pilot:open-talk-modal';

/**
 * App-wide (writer side) → PilotContext. Fired after a writer
 * (currently `PilotInviteGate`) mutates localStorage's
 * AUDIENCE_KEY or ADMIN_TOKEN_KEY so the live PilotProvider
 * re-reads + re-renders without a hard reload.
 *
 * F9 of the Oatmeal rebuild. The canonical constant lives in
 * `PilotContext.tsx` as `PILOT_AUTH_CHANGED_EVENT` (re-exported below
 * for discoverability).
 */
export const PILOT_AUTH_CHANGED_EVENT = 'pilot:auth-changed';

/* ============================================================
   React intake bridge (re-exports from intake-bridge.ts)
   ============================================================ */

/** Re-export of intake-bridge's INTAKE_CHANGED_EVENT for registry completeness. */
export const PILOT_INTAKE_CHANGED_EVENT = INTAKE_CHANGED_EVENT_RAW;

/** Re-export of intake-bridge's INTAKE_SET_REQUEST_EVENT for registry completeness. */
export const PILOT_INTAKE_SET_REQUEST_EVENT = INTAKE_SET_REQUEST_EVENT_RAW;

export type {IntakeSetRequestDetail};

/* ============================================================
   Legacy `mp:*` event names (kept verbatim until F7 retires
   them along with the imperative survivors in main.js)
   ============================================================ */

/**
 * Engine → engine + React. Fired by `main.js` when the visitor flips
 * theme (light/dark). Story.tsx listens to re-fire scene-paint so
 * time-based SVGs (lanes, histogram) re-render in the new palette.
 *
 * Listeners: `Story.tsx`.
 */
export const MP_THEME_CHANGE_EVENT = 'mp:theme-change';

export interface MpThemeChangeDetail {
  name: string;
}

/**
 * React → engine. Fired by Story scene 7's "Now run your own numbers"
 * CTA. `main.js` listens and translates into
 * `pilot:set-view-mode → sandbox` plus `loadProcess()`.
 *
 * Dispatchers: `Story.tsx` (dispatchUnlock helper).
 * Listeners: `main.js`.
 */
export const MP_UNLOCK_EVENT = 'mp:unlock';

export interface MpUnlockDetail {
  proc: unknown; // ProcessDef | null — keep as unknown to avoid a circular import
}
